// The deck's menu, and the ways out of the deck. Most take the board on
// show along, so the deck opens again on it.

export default {
  // A second window on the laptop while the room has the first: notes and
  // the clock always open, kept on the same board through the channel.
  openPresenter(event) {
    event.preventDefault();
    const url = new URL(window.location.href);
    url.searchParams.set("presenter", "1");
    url.hash = this.slideRef(this.indexValue);
    window.open(url.toString(), "carttype-presenter", "width=1100,height=700");
  },

  // The theme, switched without leaving the deck. Two themes draw a board
  // with different markup (duotone's label column, blocks' panels), so the
  // switch re-renders rather than restyles: the owner's choice is saved
  // first, then the deck reloads on the same board with the theme in the
  // URL — which is also how anyone else previews one. A new theme drops a
  // previewed typeface: the deck is drawn in the one the store picked for
  // that theme.
  async switchTheme(event) {
    const key = event.currentTarget.dataset.theme;
    const before = this.currentLook(this.themeTargets, "theme");
    this.markLook(this.themeTargets, "theme", key);
    // A theme that did not save is not chosen: the buttons go back and
    // the deck stays as it is drawn. A failed save used to leave the new
    // button marked on the old theme, with no reload to follow.
    if (!(await this.saveLook({ deck_theme: key }))) return this.markLook(this.themeTargets, "theme", before);

    const url = new URL(window.location.href);
    url.searchParams.set("theme", key);
    url.searchParams.delete("fonts");
    url.hash = this.slideRef(this.indexValue);
    window.location.replace(url.toString());
  },

  // The typeface is only an attribute on the deck — every pairing's
  // variables are already on the page — so it changes in place, on the
  // board on show, with the menu left open to try the next. The owner's
  // pick is saved for this theme behind it; anyone else previews.
  async switchFonts(event) {
    const key = event.currentTarget.dataset.fonts;
    const before = document.body.dataset.deckFonts;
    if (key === before) return;

    this.showFonts(key);
    const saved = await this.saveLook({ deck_fonts: { [document.body.dataset.deckTheme]: key } });
    // A pick that did not save goes back, unless another has replaced it.
    if (!saved && document.body.dataset.deckFonts === key) this.showFonts(before);
  },

  showFonts(key) {
    document.body.dataset.deckFonts = key;
    this.markLook(this.fontsTargets, "fonts", key);
    const url = new URL(window.location.href);
    url.searchParams.set("fonts", key);
    history.replaceState(history.state, "", url.toString());
    // Text set to fit its box reflows in the new face: fit it now, and
    // again once the face has loaded.
    const refit = () => document.body.dispatchEvent(new CustomEvent("deck:fit", { bubbles: true }));
    refit();
    document.fonts?.ready.then(refit);
  },

  currentLook(buttons, param) {
    return buttons.find((button) => button.classList.contains("is-current"))?.dataset[param];
  },

  markLook(buttons, param, value) {
    buttons.forEach((button) => {
      const current = button.dataset[param] === value;
      button.classList.toggle("is-current", current);
      button.setAttribute("aria-pressed", String(current));
    });
  },

  // The owner's choice, saved; true when there is nothing to save.
  async saveLook(account) {
    const save = this.element.dataset.presentationThemeUrl;
    if (!save) return true;

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    return fetch(save, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ account }),
    }).then((response) => response.ok, () => false);
  },

  async copyShare(event) {
    if (!this.hasShareUrlTarget) return;
    const button = event.currentTarget;
    try {
      await navigator.clipboard.writeText(this.shareUrlTarget.value);
      const label = button.textContent;
      button.textContent = button.dataset.copiedLabel;
      setTimeout(() => (button.textContent = label), 1500);
    } catch {
      this.shareUrlTarget.select();
    }
  },

  // The deck drawn again on the board on show.
  reloadHere() {
    const url = new URL(window.location.href);
    url.searchParams.delete("choose");
    url.hash = this.slideRef(this.indexValue);
    if (url.search !== window.location.search) {
      window.location.replace(url.toString());
    } else {
      history.replaceState(null, "", url.toString());
      window.location.reload();
    }
  },

  closeMenus() {
    this.element.querySelectorAll('[data-dropdown-target="menu"]').forEach((menu) => menu.classList.add("hidden"));
  },

  openCompare(event) {
    event.preventDefault();
    const link = event.currentTarget;
    // Prefer the semantic slug so the sibling deck opens on the same
    // section even if its slide numbering differs. Fall back to the
    // positional form for slides that haven't been slugged yet.
    window.open(`${link.href}#${this.slideRef(this.indexValue)}`, "_blank");
  },

  // Leaving for a page that sends the merchant back to this deck (the theme
  // picker): take the current slide along so the deck reopens on it.
  carrySlide(event) {
    event.preventDefault();
    const url = new URL(event.currentTarget.href);
    url.searchParams.set("slide", this.slideRef(this.indexValue));
    Turbo.visit(url.toString());
  },

  exit() {
    const exitUrl = this.element.dataset.presentationExitUrl;
    if (exitUrl) {
      Turbo.visit(exitUrl);
    } else {
      history.back();
    }
  },
};
